import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import countries from '@/constants/countries';

/**
 * Localized country names for the account-creation country dropdowns.
 *
 * The list itself (`constants/countries.ts`) carries English names only, so the dropdown used to
 * render "Germany" to a French user. Rather than hand-maintain 250 `Global.Countries.*` keys — a
 * standing liability, and one nobody could review — the names come from `Intl.DisplayNames`, whose
 * CLDR data ships with the browser and is already correct for every locale we serve.
 *
 * `Intl.DisplayNames({ type: 'region' })` only understands **alpha-2** codes, which is why
 * `countries.ts` carries `alpha2` alongside `alpha3`. `alpha3` stays the option *value* and the
 * only thing logic (and the submitted payload) ever sees — this changes the label and nothing else.
 *
 * Not to be confused with `useCountryLabels`, which maps the two raw English `Country` strings the
 * partner-account endpoints return onto `Global.Countries.*` for the account list tables.
 */

/** Structural shape this module needs from a `countries.ts` entry. */
type LocalizableCountry = {
  readonly name: string;
  readonly alpha2: string;
  readonly alpha3: string;
};

/** Pinned to the top of the list, in this order, ahead of the alphabetical remainder. */
const PINNED_ALPHA3 = ['CAN', 'USA'];

/**
 * i18next's `language` comes from `localStorage`/the browser, so it can be any string — and both
 * `Intl.DisplayNames` and `localeCompare` throw `RangeError` on a tag they can't parse. Canonicalize
 * once here so the rest of the module can pass the result to Intl without guarding again.
 */
export function resolveLocale(language: string): string {
  try {
    return Intl.getCanonicalLocales(language)[0] ?? 'en';
  } catch {
    return 'en';
  }
}

/**
 * The country's name in `displayNames`' locale, falling back to its English name whenever CLDR has
 * no entry for the code (`fallback: 'none'` returns `undefined`) or the code isn't a well-formed
 * region (`.of()` throws). Never returns an empty label.
 */
export function localizeCountryName(country: LocalizableCountry, displayNames: Intl.DisplayNames): string {
  try {
    return displayNames.of(country.alpha2) || country.name;
  } catch {
    return country.name;
  }
}

/**
 * `alpha3 -> localized name`, ordered the way the dropdown renders it: the pinned countries first,
 * then everything else alphabetically **by the localized name in `language`** (so the French list
 * reads alphabetically in French, e.g. Allemagne before Autriche).
 */
export function buildCountryOptions(language: string): { [alpha3: string]: string } {
  const locale = resolveLocale(language);
  const displayNames = new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' });

  const localized = countries.map((country) => ({
    alpha3: country.alpha3,
    label: localizeCountryName(country, displayNames),
  }));

  const pinned = PINNED_ALPHA3.flatMap((code) => localized.filter((option) => option.alpha3 === code));

  const rest = localized
    .filter((option) => !PINNED_ALPHA3.includes(option.alpha3))
    .sort((a, b) => a.label.localeCompare(b.label, locale));

  return Object.fromEntries([...pinned, ...rest].map(({ alpha3, label }) => [alpha3, label]));
}

export default function useCountryNames() {
  // Unprefixed on purpose: nothing here resolves a translation key — the hook only needs the
  // instance's current language, and subscribing to it is what re-renders on a language switch.
  const { i18n } = useTranslation();

  const countryOptions = useMemo(() => buildCountryOptions(i18n.language), [i18n.language]);

  return { countryOptions };
}
